import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import Box from '@mui/material/Box';
import Geosuggest, { Location, Suggest } from '@ubilabs/react-geosuggest';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '@/redux/store';
import { Countries } from '@/utils/common';

import './index.css';

import { useTheme } from '@mui/material';

interface Address extends Partial<Suggest> {
  codePostal: string | string[];
  description: string;
}

interface GeosuggestInputProps {
  placeholder: string;
  name: string;
  label: React.ReactNode;
  onChange: (name: string, suggest: Address | null) => void;
}

const GeosuggestInput = ({ placeholder, name, onChange, label }: GeosuggestInputProps) => {
  const { profile } = useSelector((state: RootState) => state.parcel);
  const country = useMemo(() => profile.data?.country, [profile.data?.country]);
  const theme = useTheme();

  const handleChange = (_value: string) => {
    onChange(name, null);
  };

  const handleSuggest = (suggest?: Location) => {
    if (suggest) {
      const cleanSuggest: Address = {
        description: suggest.label,
        location: suggest.location,
        codePostal: findAddressComponent(
          suggest.gmaps?.address_components,
          'postal_code',
          'short_name',
        ),
      };

      if (
        typeof cleanSuggest.codePostal === 'string' &&
        cleanSuggest.codePostal.trim() === '' &&
        country === 'MEX'
      ) {
        cleanSuggest.codePostal = 'MISSING';
      }
      onChange(name, cleanSuggest);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        gap: '1rem',
        width: '100%',
      }}
    >
      {label}
      <Box
        className="geosuggestBox"
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          position: 'relative',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '8px',
          backgroundColor: 'white',
          padding: '1rem 0.5rem',
          '&:hover': {
            border: `1.5px solid ${theme.palette.primary.main}`,
          },
          '&:focus-within': {
            outline: `1px solid ${theme.palette.primary.main}`,
          },
        }}
      >
        <PinDropOutlinedIcon />
        <Geosuggest
          style={{
            input: {
              width: '100%',
              border: 'none',
              color: '#43435c',
              outline: 'none',
              boxShadow: 'none',
              fontSize: '1rem',
              paddingLeft: '0.5rem',
            },
          }}
          country={handleCountrySuggestions(country!)}
          placeholder={placeholder}
          onSuggestSelect={handleSuggest}
          onChange={handleChange}
          className="customInput"
          queryDelay={850}
          minLength={4}
        />
      </Box>
    </Box>
  );
};

export default GeosuggestInput;

const handleCountrySuggestions = (country: Countries) => {
  switch (country) {
    case 'MEX':
      return 'MX';
    case 'COL':
      return 'CO';
    case 'CHL':
      return 'CL';
    case 'PER':
      return 'PE';

    default:
      return;
  }
};

const findAddressComponent = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  type: string,
  version: string,
) => {
  const address = components?.find(function (component) {
    return component.types.indexOf(type) !== -1;
  });

  if (address != undefined) {
    return address[version as keyof google.maps.GeocoderAddressComponent];
  } else {
    return '';
  }
};
